// Business-day-aware duration math for the Reporting page's SLA what-if simulator.
// Ported verbatim (logic-for-logic) from the standalone report's render.js. Europe/Brussels,
// Mon-Fri 09:00-17:00, Belgian public holidays (10/year: 7 fixed + 3 Easter-derived).

export type DayMode = 'all' | 'business';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function belgianHolidaysForYear(year: number): Set<string> {
  const set = new Set<string>();
  const add = (m: number, d: number) => set.add(`${year}-${pad2(m)}-${pad2(d)}`);
  add(1, 1);
  add(5, 1);
  add(7, 21);
  add(8, 15);
  add(11, 1);
  add(11, 11);
  add(12, 25);
  const easter = easterSunday(year);
  [1, 39, 50].forEach((offsetDays) => {
    const dt = new Date(easter.getTime() + offsetDays * 86400000);
    set.add(`${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`);
  });
  return set;
}

function buildHolidaySet(years: number[]): Set<string> {
  const set = new Set<string>();
  const wanted = new Set<number>();
  years.forEach((y) => {
    wanted.add(y);
    wanted.add(y - 1);
    wanted.add(y + 1);
  });
  wanted.forEach((y) => belgianHolidaysForYear(y).forEach((d) => set.add(d)));
  return set;
}

interface BrusselsParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function brusselsParts(date: Date): BrusselsParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Brussels',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, number> = {};
  dtf.formatToParts(date).forEach((p) => {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  });
  if (parts.hour === 24) parts.hour = 0;
  return parts as unknown as BrusselsParts;
}

function brusselsLocalToUtc(year: number, month: number, day: number, hour: number, minute: number): Date {
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const p = brusselsParts(guess);
  const asUTC = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  const offset = asUTC - guess.getTime();
  return new Date(guess.getTime() - offset);
}

export function businessHoursBetween(startStr: string, endStr: string, holidaySet: Set<string>): number {
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (end <= start) return 0;
  let total = 0;
  let p = brusselsParts(start);
  let y = p.year;
  let m = p.month;
  let d = p.day;
  for (let guard = 0; guard < 1500; guard++) {
    const dateStr = `${y}-${pad2(m)}-${pad2(d)}`;
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    const dayStart = brusselsLocalToUtc(y, m, d, 9, 0);
    const dayEnd = brusselsLocalToUtc(y, m, d, 17, 0);
    if (weekday !== 0 && weekday !== 6 && !holidaySet.has(dateStr)) {
      const segStart = start > dayStart ? start : dayStart;
      const segEnd = end < dayEnd ? end : dayEnd;
      if (segEnd > segStart) total += (segEnd.getTime() - segStart.getTime()) / 3600000;
    }
    const nextDay = new Date(Date.UTC(y, m - 1, d + 1));
    const nextDayStartUtc = brusselsLocalToUtc(nextDay.getUTCFullYear(), nextDay.getUTCMonth() + 1, nextDay.getUTCDate(), 0, 0);
    if (nextDayStartUtc >= end) break;
    y = nextDay.getUTCFullYear();
    m = nextDay.getUTCMonth() + 1;
    d = nextDay.getUTCDate();
  }
  return total;
}

export function makeHoursBetween(dayMode: DayMode, referenceYears: number[]) {
  const holidaySet = dayMode === 'business' ? buildHolidaySet(referenceYears) : new Set<string>();
  return function hoursBetween(startStr: string, endStr: string): number {
    if (dayMode === 'business') return businessHoursBetween(startStr, endStr, holidaySet);
    return (new Date(endStr).getTime() - new Date(startStr).getTime()) / 3600000;
  };
}

// 1 business day = 09:00-17:00 = 8h; 1 calendar day = 24h.
export function toDays(hours: number | null, dayMode: DayMode): number | null {
  if (hours == null) return null;
  return hours / (dayMode === 'business' ? 8 : 24);
}

export function median(nums: number[]): number | null {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function percentile(nums: number[], p: number): number | null {
  if (!nums.length) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const idx = p * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

export function p70(nums: number[]): number | null {
  return percentile(nums, 0.7);
}

export function p90(nums: number[]): number | null {
  return percentile(nums, 0.9);
}

export function mean(nums: number[]): number | null {
  return nums.length ? nums.reduce((s, h) => s + h, 0) / nums.length : null;
}
